import { DocumentVersionStatus } from '@qualicloud/contracts';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { DocumentStatusBadge } from '@/components/validity-semaphore';
import {
  Button,
  Card,
  CardHeader,
  Combobox,
  DatePicker,
  EmptyState,
  Field,
  IconAlertTriangle,
  IconEye,
  IconFiles,
  IconHistory,
  IconSearch,
  IconShieldCheck,
  Input,
  Pagination,
  Select,
  Table,
  type TableColumn,
} from '@/design-system';
import { documentTypesApi, documentsApi, queryKeys, toUserMessage } from '@/lib/api';
import type { AdminDocumentFilter } from '@/lib/api';
import type { AdminDocumentRow, DocumentTypeNode } from '@/lib/api/types';
import { formatDateOnly } from '@/lib/format/datetime';
import { DOCUMENT_STATUS_LABEL } from '@/lib/i18n';
import { useDirectory } from '@/lib/use-directory';
import type { DocumentAdminSearch } from '@/routes/search-schemas';
import { PageBody, PageHeader } from '@/shell/app-shell';

/** 50 por página, o mesmo número que a tela Administração do legado (`ged.php:32`). */
const ADMIN_PAGE_SIZE = 50;

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ADMINISTRAÇÃO DE DOCUMENTOS — equivalente a `ged/ged/admin`
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * O QUE ESTA TELA É (ged.md §14.3, §15.2, §16.10)
 * Listagem de TODOS os documentos da organização, ignorando as regras de acesso
 * de cada documento. É a visão que o gestor da qualidade usa para responder
 * "existe algum documento que ninguém está vendo?" — pergunta que a Lista
 * Mestra, por construção, não consegue responder: ela só mostra o que a ACL
 * libera para quem está olhando.
 *
 * O QUE ELA NÃO É
 * Não é um bypass de tenant. O legado tinha `filter_store_id`, e o admin global
 * podia listar documentos de qualquer empresa (R4/R5). Aqui a organização vem
 * do JWT verificado na borda e não existe parâmetro equivalente: nem na URL,
 * nem na API. O acesso do fornecedor a uma organização cliente exige elevação
 * própria, temporária e auditada.
 *
 * TRANSPARÊNCIA
 * Ignorar a ACL é poder, e poder silencioso é achado de auditoria. A tela
 * declara, antes do resultado, que a listagem ignora as regras de acesso e que
 * a consulta é registrada na trilha — do mesmo jeito que a exportação da Lista
 * Mestra declara.
 *
 * PERMISSÃO
 * `document:read:all`. A rota é protegida pelo `<ProtectedRoute>` e a API
 * reavalia no servidor: esconder o item de menu não protege rota nenhuma.
 */
export function DocumentAdminPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as unknown as DocumentAdminSearch;
  const directory = useDirectory();

  const filter = useMemo<AdminDocumentFilter>(
    () => ({
      page: search.page ?? 1,
      pageSize: search.pageSize ?? ADMIN_PAGE_SIZE,
      search: search.search,
      documentTypeId: search.documentTypeId,
      authorUserId: search.authorUserId,
      status: search.status as DocumentVersionStatus[] | undefined,
      createdFrom: search.createdFrom,
      createdTo: search.createdTo,
    }),
    [search],
  );

  const updateFilter = useCallback(
    (patch: Partial<DocumentAdminSearch>) => {
      void navigate({
        to: '/documentos/administracao',
        search: { ...search, ...patch, page: patch.page ?? 1 } as DocumentAdminSearch,
      });
    },
    [navigate, search],
  );

  const typesQuery = useQuery({
    queryKey: queryKeys.documentTypes.tree(),
    queryFn: () => documentTypesApi.tree(),
    staleTime: 5 * 60_000,
  });

  const listQuery = useQuery({
    queryKey: queryKeys.documents.admin(filter as unknown as Record<string, unknown>),
    queryFn: () => documentsApi.adminList(filter),
    placeholderData: keepPreviousData,
  });

  const typeOptions = useMemo(() => flattenTypes(typesQuery.data ?? []), [typesQuery.data]);

  const columns = useMemo<Array<TableColumn<AdminDocumentRow>>>(
    () => [
      {
        id: 'name',
        header: 'Nome do documento',
        width: '30%',
        cell: (row) => (
          <Link
            to="/documentos/$documentId"
            params={{ documentId: row.documentId }}
            className="block truncate font-medium text-brand hover:underline"
          >
            {row.code ? <span className="font-mono text-xs">{row.code} · </span> : null}
            {row.name}
          </Link>
        ),
      },
      {
        id: 'documentType',
        header: 'Tipo',
        width: '15%',
        cell: (row) => <span className="text-content-muted">{row.documentType}</span>,
      },
      {
        // Coluna exclusiva desta tela: sem a ACL para orientar, o autor é a
        // única pista de origem do documento.
        id: 'author',
        header: 'Autor',
        width: '16%',
        cell: (row) => (
          <span className="text-content">{row.authorName ?? 'Usuário não identificado'}</span>
        ),
      },
      {
        id: 'version',
        header: 'Versão',
        width: '7%',
        align: 'right',
        cell: (row) => <span className="qc-tabular">{row.version}</span>,
      },
      {
        id: 'revisionDate',
        header: 'Data da versão',
        width: '11%',
        cell: (row) => <span className="qc-tabular">{formatDateOnly(row.revisionDate)}</span>,
      },
      {
        id: 'validUntil',
        header: 'Válido até',
        width: '11%',
        cell: (row) => (
          <span className="qc-tabular">
            {row.validUntil ? formatDateOnly(row.validUntil) : 'Sem validade'}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Situação',
        width: '10%',
        cell: (row) => <DocumentStatusBadge status={row.status} />,
      },
    ],
    [],
  );

  const activeFilterCount = countActiveFilters(search);

  return (
    <>
      <PageHeader
        breadcrumbs={[{ label: 'Lista Mestra', to: '/documentos' }, { label: 'Administração' }]}
        title="Administração de documentos"
        description="Todos os documentos da organização, independentemente das regras de acesso de cada um."
        actions={
          <Button asChild variant="secondary" leadingIcon={<IconHistory size={15} />}>
            <Link to="/auditoria" search={{ entityType: 'Document', page: 1 }}>
              Ver a trilha de documentos
            </Link>
          </Button>
        }
      />

      <PageBody className="flex flex-col gap-4">
        {/*
          O aviso vem ANTES do resultado, é texto (não só cor) e nomeia as duas
          coisas que o usuário precisa saber: a listagem ignora a ACL, e o
          acesso fica registrado.
        */}
        <div
          role="note"
          aria-labelledby="admin-scope-title"
          className="flex items-start gap-3 rounded-lg border border-warning bg-warning-subtle px-4 py-3"
        >
          <IconShieldCheck size={20} aria-hidden="true" className="mt-0.5 shrink-0 text-warning-content" />
          <div className="min-w-0">
            <p id="admin-scope-title" className="text-sm font-semibold text-warning-content">
              Visão administrativa — as regras de acesso dos documentos não são aplicadas
            </p>
            <p className="mt-1 text-sm text-warning-content">
              Esta lista mostra documentos que você não veria na Lista Mestra, incluindo os de outros
              departamentos. Ela existe para gestão do acervo, não para leitura corrente. Cada
              consulta é registrada na trilha de auditoria com o seu usuário, o horário e os filtros
              aplicados. O isolamento entre organizações continua valendo: só aparecem documentos da
              sua organização.
            </p>
          </div>
        </div>

        <Card>
          <CardHeader
            headingLevel={2}
            title="Filtros"
            description="Os filtros são combináveis."
            action={
              activeFilterCount > 0 ? (
                <Button
                  variant="ghost"
                  onClick={() =>
                    void navigate({ to: '/documentos/administracao', search: { page: 1 } })
                  }
                >
                  Limpar filtros
                </Button>
              ) : undefined
            }
          />

          <div className="mt-4 grid gap-4 lg:grid-cols-3">
            {/*
              `key` amarrado ao valor da URL: ao limpar os filtros, o campo
              precisa esvaziar junto. Sem isso o texto continuaria na caixa
              sugerindo um filtro que não está mais aplicado.
            */}
            <SearchField
              key={search.search ?? ''}
              value={search.search ?? ''}
              onSubmit={(term) => updateFilter({ search: term })}
            />

            <Field label="Tipo de documento">
              <Select
                value={search.documentTypeId ?? '__all__'}
                onValueChange={(value) =>
                  updateFilter({ documentTypeId: value === '__all__' ? undefined : value })
                }
                options={[
                  { value: '__all__', label: 'Todos os tipos' },
                  ...typeOptions.map((type) => ({ value: type.id, label: type.path })),
                ]}
              />
            </Field>

            <Field
              label="Autor"
              description={
                directory.forbidden
                  ? 'Exige a permissão de gestão de usuários para listar os colaboradores.'
                  : undefined
              }
            >
              <Combobox
                items={directory.subjects.map((subject) => ({
                  value: subject.userId,
                  label: subject.displayName,
                  hint: subject.jobTitle,
                }))}
                value={search.authorUserId ?? null}
                loading={directory.loading}
                disabled={directory.forbidden}
                clearable
                onValueChange={(userId) => updateFilter({ authorUserId: userId ?? undefined })}
                placeholder="Qualquer autor"
                emptyMessage="Nenhum colaborador encontrado."
              />
            </Field>

            <Field label="Situação">
              <Select
                value={search.status?.[0] ?? '__all__'}
                onValueChange={(value) =>
                  updateFilter({ status: value === '__all__' ? undefined : [value] })
                }
                options={[
                  { value: '__all__', label: 'Todas as situações' },
                  ...Object.values(DocumentVersionStatus).map((status) => ({
                    value: status,
                    label: DOCUMENT_STATUS_LABEL[status],
                  })),
                ]}
              />
            </Field>

            <Field label="Criado a partir de">
              <DatePicker
                value={search.createdFrom ?? ''}
                onValueChange={(iso) => updateFilter({ createdFrom: iso || undefined })}
              />
            </Field>

            <Field label="Criado até">
              <DatePicker
                value={search.createdTo ?? ''}
                onValueChange={(iso) => updateFilter({ createdTo: iso || undefined })}
              />
            </Field>
          </div>
        </Card>

        {listQuery.isError ? (
          <p
            role="alert"
            className="flex items-start gap-2 rounded-md border border-danger bg-danger-subtle px-3 py-2.5 text-sm text-danger-content"
          >
            <IconAlertTriangle size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
            <span>{toUserMessage(listQuery.error)}</span>
          </p>
        ) : null}

        <div>
          <Table<AdminDocumentRow>
            caption="Listagem administrativa de documentos da organização. Colunas: nome, tipo, autor, versão, data da versão, validade e situação."
            columns={columns}
            rows={listQuery.data?.items ?? []}
            rowKey={(row) => row.documentId}
            loading={listQuery.isPending}
            empty={
              <EmptyState
                icon={<IconFiles size={22} />}
                title={
                  activeFilterCount > 0
                    ? 'Nenhum documento corresponde aos filtros'
                    : 'Nenhum documento cadastrado na organização'
                }
                description={
                  activeFilterCount > 0
                    ? 'Ajuste ou limpe os filtros para ampliar a busca. Esta listagem já ignora as regras de acesso, então o vazio aqui significa que o documento não existe — e não que você não pode vê-lo.'
                    : 'Assim que o primeiro documento controlado for criado, ele aparecerá aqui, junto com o nome de quem o criou.'
                }
                action={
                  activeFilterCount > 0 ? (
                    <Button
                      variant="secondary"
                      onClick={() =>
                        void navigate({ to: '/documentos/administracao', search: { page: 1 } })
                      }
                    >
                      Limpar filtros
                    </Button>
                  ) : (
                    <Button asChild variant="secondary" leadingIcon={<IconEye size={15} />}>
                      <Link to="/documentos">Ir para a Lista Mestra</Link>
                    </Button>
                  )
                }
              />
            }
          />

          {listQuery.data && listQuery.data.total > 0 ? (
            <div className="rounded-b-lg border-x border-b border-border bg-surface">
              <Pagination
                page={listQuery.data.page}
                pageSize={listQuery.data.pageSize}
                total={listQuery.data.total}
                itemNoun="documentos"
                onPageChange={(page) => updateFilter({ page })}
                onPageSizeChange={(pageSize) => updateFilter({ pageSize, page: 1 })}
              />
            </div>
          ) : null}
        </div>
      </PageBody>
    </>
  );
}

function SearchField({
  value,
  onSubmit,
}: {
  readonly value: string;
  readonly onSubmit: (term: string | undefined) => void;
}) {
  const [term, setTerm] = useState(value);

  return (
    <form
      role="search"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(term.trim() || undefined);
      }}
    >
      <Field label="Nome ou código">
        <Input
          type="search"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Buscar e pressionar Enter…"
          leadingIcon={<IconSearch size={15} />}
        />
      </Field>
    </form>
  );
}

function flattenTypes(nodes: readonly DocumentTypeNode[]): Array<{ id: string; path: string }> {
  const result: Array<{ id: string; path: string }> = [];
  const walk = (list: readonly DocumentTypeNode[]) => {
    for (const node of list) {
      result.push({ id: node.id, path: node.path || node.name });
      if (node.children.length > 0) walk(node.children);
    }
  };
  walk(nodes);
  return result;
}

function countActiveFilters(search: DocumentAdminSearch): number {
  let count = 0;
  if (search.search) count += 1;
  if (search.documentTypeId) count += 1;
  if (search.authorUserId) count += 1;
  if (search.status && search.status.length > 0) count += 1;
  if (search.createdFrom) count += 1;
  if (search.createdTo) count += 1;
  return count;
}
